import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "../data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("hero");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    NAV.forEach((n) => {
      const el = document.getElementById(n.id);
      if (el) obs.observe(el);
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      obs.disconnect();
    };
  }, []);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={
          "fixed inset-x-0 top-0 z-[9000] transition-colors duration-500 " +
          (scrolled ? "bg-navy-900/80 backdrop-blur-md border-b border-gold/10" : "bg-transparent")
        }
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 sm:px-12">
          <a href="#hero" data-testid="brand-logo" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Aarambh Buildworks" className="h-9 w-auto sm:h-10" />
            <span className="font-heading text-2xl font-medium tracking-wide text-offwhite">Aarambh</span>
            <span className="hidden text-[10px] uppercase tracking-[0.3em] text-gold sm:inline">Buildworks</span>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`nav-${n.id}`}
                className="relative text-[11px] uppercase tracking-[0.2em] text-offwhite/70 transition-colors duration-300 hover:text-gold"
              >
                {n.label}
                {active === n.id && (
                  <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 h-px w-full bg-gold" />
                )}
              </a>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            className="text-offwhite md:hidden"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9500] flex flex-col bg-navy-900 px-8 py-6 md:hidden"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/logo-mark.png" alt="" className="h-8 w-auto" />
                <span className="font-heading text-2xl text-offwhite">Aarambh</span>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-gold" aria-label="Close menu">
                <X size={26} />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-6">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.id}
                  href={`#${n.id}`}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  className="font-heading text-4xl font-light text-offwhite"
                >
                  {n.label}
                </motion.a>
              ))}
            </nav>
            <p className="mt-auto text-xs uppercase tracking-[0.2em] text-gold">{BRAND.tagline}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
